import { createSlice } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import { AInvoiceSliceState } from "../../utils/invoice.types";
import { ApiStatus } from "../../utils/api.types";
import { fetchInvoiceSeqNumber, addInvoice, deleteInvoice, deletePaymentReceipt, fetchInvoices, updateInvoice, updateInvoiceStatus, fetchInvoiceById, dispatchInvoice, manualPayment, shareInvoicePaymentReceipt, fetchInvoicesWithoutState } from "./invoiceThunk";

/**
 * Invoice slice initial state.
 */
const initialState: AInvoiceSliceState = {
    invoices: [],
    rowCount: 0,
    invoiceDetails: {},
    errorMessage: '',
    isInvoiceCreated: false,
    selectedInvoiceId: '',
    status: ApiStatus.IDLE
}

/**
 * Invoice Slice.
 */
const invoiceSlice = createSlice({
    name: 'invoice',
    initialState: initialState,
    reducers: {
        // Selected Invoice id
        //selectedInvoiceId: (state, action) => {
        // state.selectedInvoiceId = action.payload || '';
        // }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Invoices
            .addCase(fetchInvoices.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchInvoices.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.invoices = action?.payload.invoices || [];
                state.rowCount = action?.payload.total || 0;
                state.invoiceDetails = {};
                state.errorMessage = '';
            })

            .addCase(fetchInvoices.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })

            // Fetch invoice seq number
            .addCase(fetchInvoiceSeqNumber.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchInvoiceSeqNumber.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
            })

            .addCase(fetchInvoiceSeqNumber.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })

            // Fetch Invoice by ID
            .addCase(fetchInvoiceById.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchInvoiceById.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.invoiceDetails = action.payload || [];
                state.errorMessage = '';
            })
            .addCase(fetchInvoiceById.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
            })

            // Add Invoice
            .addCase(addInvoice.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(addInvoice.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.isInvoiceCreated = true;
                state.invoices.push(action.payload.invoice);
                state.errorMessage = '';

                toast.success(action.payload.message);
            })
            .addCase(addInvoice.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.isInvoiceCreated = false;
                state.errorMessage = action.error.message
            })

            // Delete Invoice
            .addCase(deleteInvoice.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deleteInvoice.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';

                const { id, message } = action.payload;
                state.invoices = state.invoices.filter(item => item._id !== id)

                toast.success(message);
            })
            .addCase(deleteInvoice.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message

                toast.success(state.errorMessage);
            })

            // Delete Invoice Payment Receipt
            .addCase(deletePaymentReceipt.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(deletePaymentReceipt.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';

                const { message } = action.payload;
                toast.success(message);
            })
            .addCase(deletePaymentReceipt.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message

                toast.success(state.errorMessage);
            })

            // Update Invoice
            .addCase(updateInvoice.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateInvoice.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message, invoice: updatedInvoice } = action.payload
                state.invoices = state.invoices
                    .map(item => (item._id === item._id ? { ...item, ...updatedInvoice } : item));

                state.errorMessage = '';

                toast.success(message);
            })
            .addCase(updateInvoice.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
            })

            // Update Invoice Status
            .addCase(updateInvoiceStatus.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(updateInvoiceStatus.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                const { message, invoice: updateInvoice } = action.payload
                state.invoices = state.invoices.map(item => (item._id === item._id ? { ...item, ...updateInvoice } : item));
                state.errorMessage = '';
                toast.success(message);
            })
            .addCase(updateInvoiceStatus.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
            })


            // Dispatch Invoice
            .addCase(dispatchInvoice.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(dispatchInvoice.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
                const { message } = action.payload;
                toast.success(message);
            })
            .addCase(dispatchInvoice.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
                toast.success(state.errorMessage);
            })
            .addCase(manualPayment.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(manualPayment.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
                const { message } = action.payload;
                toast.success(message);
            })
            .addCase(manualPayment.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
                toast.success(state.errorMessage);
            })
            //  Share Invoice Payment Receipt
            .addCase(shareInvoicePaymentReceipt.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(shareInvoicePaymentReceipt.fulfilled, (state, action) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
                const { message } = action.payload;
                toast.success(message);
            })
            .addCase(shareInvoicePaymentReceipt.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
                toast.error(state.errorMessage);
            })
            // Fetch invoice without saving to state
            .addCase(fetchInvoicesWithoutState.pending, (state, action) => {
                state.status = ApiStatus.LOADING;
            })
            .addCase(fetchInvoicesWithoutState.fulfilled, (state, action: any) => {
                state.status = ApiStatus.SUCCESS;
                state.errorMessage = '';
                const { message } = action.payload;
                toast.success(message);
            })
            .addCase(fetchInvoicesWithoutState.rejected, (state, action) => {
                state.status = ApiStatus.FAILED;
                state.errorMessage = action.error.message
                toast.error(state.errorMessage);
            })

            
    }
});

export default invoiceSlice.reducer;
